import { Injectable, computed, effect, inject, signal } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Observable, catchError, map, of } from 'rxjs'
import { environment } from '../../environments/environment'
import { AuthService } from './auth.service'
import { serializeCustomFieldFilters } from '../helpers/custom-fields'
import {
  MarketplaceFilters,
  MarketplaceProduct,
  MarketplaceResult,
} from '../types/marketplace'
import {
  CustomFieldOptions,
  CustomFieldSearchResult,
  CustomFieldSummary,
} from '../types/product-custom-fields'

@Injectable({ providedIn: 'root' })
export class MarketplaceService {
  /** Stores each listing's [horizontal, vertical] page position for Back/Forward navigation. */
  readonly detailScrollPositions = new Map<string, [number, number]>()
  private http = inject(HttpClient)
  private auth = inject(AuthService)
  private url = environment.baseUrl + 'products/marketplace'
  private cartUrl = environment.baseUrl + 'requisition-cart'
  cartItems = signal<RequisitionCartItem[]>([])
  cartGenerationId = signal<string | null>(null)
  cartRevision = signal<number | null>(null)
  cartCount = computed(() => this.cartItems().length)
  cartLoading = signal(false)
  cartError = signal<RequisitionCartError | null>(null)
  cartLoadFailed = signal(false)
  pendingId = signal<string | null>(null)
  private loadedFor?: string
  private loadVersion = 0

  constructor() {
    effect(() => {
      if (this.auth.isLoggedIn()) {
        this.loadCart()
      } else {
        this.loadVersion++
        this.loadedFor = undefined
        this.cartItems.set([])
        this.cartGenerationId.set(null)
        this.cartRevision.set(null)
        this.pendingId.set(null)
        this.cartLoading.set(false)
        this.cartError.set(null)
        this.cartLoadFailed.set(false)
      }
    })
  }

  private ownerKey(): string | undefined {
    const session = this.auth.getSessionFromLS()
    return session?.user.id
      ? `${session.user.id}:${session.sessionId}`
      : undefined
  }

  loadCart(force = false): void {
    const session = this.auth.getSessionFromLS()
    const owner = this.ownerKey()
    if (
      !session ||
      !owner ||
      !['Admin', 'Compliance Officer'].includes(session.user.role)
    )
      return
    if (!force && this.loadedFor === owner) return
    if (this.loadedFor && this.loadedFor !== owner) {
      this.cartItems.set([])
      this.cartGenerationId.set(null)
      this.cartRevision.set(null)
    }
    this.loadedFor = owner
    const version = ++this.loadVersion
    this.cartLoading.set(true)
    this.cartError.set(null)
    this.cartLoadFailed.set(false)
    this.http.get<RequisitionCart>(this.cartUrl).subscribe({
      next: (cart) => {
        if (this.ownerKey() !== owner || version !== this.loadVersion) return
        this.cartItems.set(cart.items)
        this.cartGenerationId.set(cart.generationId)
        this.cartRevision.set(cart.revision)
        this.cartLoading.set(false)
      },
      error: () => {
        if (this.ownerKey() !== owner || version !== this.loadVersion) return
        this.loadedFor = undefined
        this.cartLoadFailed.set(true)
        this.cartLoading.set(false)
      },
    })
  }

  canUseCart(): boolean {
    const role = this.auth.getSessionFromLS()?.user.role
    return role === 'Admin' || role === 'Compliance Officer'
  }

  /**
   * Fetches a filtered product page from GET /products/marketplace.
   * @param {MarketplaceFilters} filters Search criteria, selected attributes and pagination.
   * @returns {Observable<MarketplaceResult>} Products, total count and available filter options.
   */
  browse(filters: MarketplaceFilters): Observable<MarketplaceResult> {
    let params = new HttpParams()
    for (const [key, value] of Object.entries(filters)) {
      if (key === 'customFields') {
        const customFields = serializeCustomFieldFilters(filters.customFields)
        if (customFields !== '[]') params = params.set(key, customFields)
      } else if (Array.isArray(value)) {
        for (const item of value) params = params.append(key, String(item))
      } else if (value !== null && value !== '') {
        params = params.set(key, String(value))
      }
    }
    return this.http.get<MarketplaceResult>(this.url, { params })
  }

  /**
   * Finds available attributes through GET /products/marketplace/custom-fields.
   * @param {string} query Attribute name search; an empty query returns initial suggestions.
   * @returns {Observable<CustomFieldSearchResult>} Matching attributes and whether more are available.
   */
  searchCustomFields(query: string): Observable<CustomFieldSearchResult> {
    return this.http.get<CustomFieldSearchResult>(`${this.url}/custom-fields`, {
      params: new HttpParams().set('q', query.trim()),
    })
  }

  /**
   * Fetches attribute options from GET /products/marketplace/custom-field-options.
   * @param {CustomFieldSummary} field Attribute identified by its name, type and optional unit.
   * @param {string} [query] Optional search within text values.
   * @returns {Observable<CustomFieldOptions>} Available values or numeric bounds for the attribute.
   */
  customFieldOptions(
    field: CustomFieldSummary,
    query = '',
  ): Observable<CustomFieldOptions> {
    let params = new HttpParams()
      .set('name', field.name)
      .set('type', field.type)
      .set('q', query.trim())
    if (field.unit) params = params.set('unit', field.unit)
    return this.http.get<CustomFieldOptions>(
      `${this.url}/custom-field-options`,
      {
        params,
      },
    )
  }

  /**
   * Fetches product details from GET /products/marketplace/:id.
   * @param {string} id Marketplace product ID.
   * @returns {Observable<MarketplaceProduct>} Product details, including custom fields.
   */
  get(id: string): Observable<MarketplaceProduct> {
    return this.http.get<MarketplaceProduct>(
      `${this.url}/${encodeURIComponent(id)}`,
    )
  }

  /**
   * Fetches visible direct components from GET /products/marketplace/:id/components.
   * @param {string} id Parent marketplace product ID.
   * @returns {Observable<MarketplaceProduct[]>} Linked products the buyer can view, or an empty list when none are visible.
   */
  getComponents(id: string): Observable<MarketplaceProduct[]> {
    return this.http.get<MarketplaceProduct[]>(
      `${this.url}/${encodeURIComponent(id)}/components`,
    )
  }

  wholeUnit(requestUnit: string | null | undefined): boolean {
    const unit = requestUnit?.trim().toLowerCase() || 'each'
    return unit === 'each' || unit === 'pack'
  }

  validQuantity(
    quantity: number,
    requestUnit: string | null | undefined,
  ): boolean {
    return (
      Number.isFinite(quantity) &&
      quantity > 0 &&
      (!this.wholeUnit(requestUnit) || Number.isInteger(quantity))
    )
  }

  toggleSelection(product: MarketplaceProduct, quantity = 1): void {
    if (
      this.pendingId() ||
      (!this.isSelected(product.id) &&
        (!product.allowRequests ||
          !this.validQuantity(quantity, product.requestUnit)))
    )
      return
    this.changeSelection(
      product.id,
      this.isSelected(product.id) ? 'delete' : 'put',
      quantity,
    )
  }

  removeSelection(id: string): void {
    if (!this.pendingId()) this.changeSelection(id, 'delete')
  }

  updateQuantity(id: string, quantity: number): Observable<boolean> {
    const owner = this.ownerKey()
    if (!owner || this.pendingId()) {
      this.cartError.set('save')
      return of(false)
    }
    this.pendingId.set(id)
    this.cartError.set(null)
    return this.http
      .patch<void>(`${this.cartUrl}/items/${encodeURIComponent(id)}`, {
        quantity,
      })
      .pipe(
        map(() => {
          if (this.ownerKey() !== owner) return false
          this.pendingId.set(null)
          this.loadCart(true)
          return true
        }),
        catchError((error: HttpErrorResponse) => {
          if (this.ownerKey() === owner) {
            const reason =
              error.status === 409 ? error.error?.reason : undefined
            this.cartError.set(
              reason === 'unavailable' || reason === 'requests_disabled'
                ? reason
                : 'save',
            )
            this.pendingId.set(null)
          }
          return of(false)
        }),
      )
  }

  private changeSelection(
    id: string,
    method: 'put' | 'delete',
    quantity = 1,
  ): void {
    const owner = this.ownerKey()
    if (!owner) return
    this.pendingId.set(id)
    this.cartError.set(null)
    this.http
      .request<void>(
        method,
        `${this.cartUrl}/items/${encodeURIComponent(id)}`,
        {
          body: method === 'put' ? { quantity } : undefined,
        },
      )
      .subscribe({
        next: () => {
          if (this.ownerKey() !== owner) return
          this.pendingId.set(null)
          this.loadCart(true)
        },
        error: (error: HttpErrorResponse) => {
          if (this.ownerKey() !== owner) return
          const reason = error.status === 409 ? error.error?.reason : undefined
          this.cartError.set(
            reason === 'unavailable' ||
              reason === 'requests_disabled' ||
              reason === 'own_product'
              ? reason
              : 'save',
          )
          this.pendingId.set(null)
        },
      })
  }

  isSelected(id: string): boolean {
    return this.cartItems().some((item) => item.productId === id)
  }
}

export type RequisitionCartItem = {
  productId: string
  quantity: number
  product?: MarketplaceProduct
  reason?: 'unavailable' | 'requests_disabled'
}

export type RequisitionCart = {
  generationId: string
  revision: number
  items: RequisitionCartItem[]
}

export type RequisitionCartError =
  'save' | 'unavailable' | 'requests_disabled' | 'own_product'
